import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Sparkles, Pencil, Archive, RotateCcw, Trash2 } from "lucide-react";
import { api, currentUser } from "@/lib/api";
import { Pill, inr, fmtDate, errMsg, DARK_DIALOG } from "./shared";
import { EditBookingDialog, SoftDeleteDialog, HardDeleteDialog } from "./BookingDialogs";

export default function BookingDetailSheet({ id, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [dlg, setDlg] = useState(null);
  const me = currentUser() || {};

  const load = () => api.get(`/admin/manage/bookings/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setD(null); if (id) load(); }, [id]);

  const done = (closeSheet) => { setDlg(null); onChanged(); closeSheet ? onClose() : load(); };
  const restore = async () => {
    try { await api.post(`/admin/manage/bookings/${id}/restore`); toast.success("Booking restored"); done(false); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const dispatch = async () => {
    try { const { data } = await api.post(`/admin/dispatch/${id}`); toast.success(`Assigned ${data.assigned}/${data.needed}`); done(false); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const b = d?.booking;
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className={`${DARK_DIALOG} w-full sm:max-w-xl overflow-y-auto`} data-testid="bm-detail-sheet">
        <SheetHeader><SheetTitle className="text-slate-100 font-mono">{b?.code || "Loading…"}</SheetTitle></SheetHeader>
        {b && (
          <div className="mt-4 space-y-5 text-sm">
            <div className="flex flex-wrap gap-2">{b.is_deleted && <Pill value="deleted" testid="bm-detail-deleted"/>}<Pill value={b.status} testid="bm-detail-status"/><Pill value={b.payment_status}/></div>
            {b.is_deleted && <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-200" data-testid="bm-deleted-banner">Deleted {fmtDate(b.deleted_at)} · Reason: {b.delete_reason}</div>}
            <Grid rows={[["Customer", b.customer_name], ["Phone", b.customer_phone], ["Email", b.customer_email || "—"], ["Service", b.pricing?.service_label],
              ["Workers", `${b.assigned_count || 0}/${b.worker_count}`], ["When", `${b.scheduled_date} · ${b.start_time} · ${b.duration_hours}h`],
              ["Location", b.location], ["Amount", inr(b.amount)], ["Paid", inr(b.amount_paid)], ["Created", fmtDate(b.created_at)]]}/>
            {b.work_description && <p className="text-slate-400 text-xs"><b className="text-slate-500">Work:</b> {b.work_description}</p>}
            {b.admin_note && <p className="text-amber-300 text-xs" data-testid="bm-detail-note"><b>Admin note:</b> {b.admin_note}</p>}
            <Actions b={b} me={me} setDlg={setDlg} restore={restore} dispatch={dispatch}/>
            {!b.is_deleted && <CancelQuote id={b.id}/>}
            <Section title={`Payments (${d.payments.length})`} rows={d.payments.map((p) => [p.payment_type, inr(p.amount), p.status, p.gateway])}/>
            <Section title={`Refunds (${d.refunds.length})`} rows={d.refunds.map((r) => [inr(r.amount), r.status, r.reason])}/>
            <Section title="Activity" rows={d.audit.slice(0, 15).map((a) => [fmtDate(a.ts), a.action])} testid="bm-activity"/>
          </div>
        )}
        {dlg === "edit" && <EditBookingDialog b={b} onClose={() => setDlg(null)} onDone={() => done(false)}/>}
        {dlg === "soft" && <SoftDeleteDialog b={b} onClose={() => setDlg(null)} onDone={() => done(true)}/>}
        {dlg === "hard" && <HardDeleteDialog b={b} onClose={() => setDlg(null)} onDone={() => done(true)}/>}
      </SheetContent>
    </Sheet>
  );
}

function Actions({ b, me, setDlg, restore, dispatch }) {
  const btn = "border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800";
  const paid = ["successful", "advance_paid", "fully_paid"].includes(b.payment_status);
  return (
    <div className="flex flex-wrap gap-2 pt-1">
      {!b.is_deleted && <Button size="sm" variant="outline" className={btn} onClick={() => setDlg("edit")} data-testid="bm-edit-btn"><Pencil size={14} className="mr-1"/>Edit</Button>}
      {!b.is_deleted && paid && <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={dispatch} data-testid="bm-dispatch-btn"><Sparkles size={14} className="mr-1"/>AI Dispatch</Button>}
      {!b.is_deleted && <Button size="sm" variant="outline" className="border-amber-500/40 bg-transparent text-amber-300 hover:bg-amber-500/10" onClick={() => setDlg("soft")} data-testid="bm-soft-delete-btn"><Archive size={14} className="mr-1"/>Delete</Button>}
      {b.is_deleted && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={restore} data-testid="bm-restore-btn"><RotateCcw size={14} className="mr-1"/>Restore</Button>}
      {b.is_deleted && me.is_primary_super_admin && <Button size="sm" variant="outline" className="border-red-500/50 bg-transparent text-red-300 hover:bg-red-500/10" onClick={() => setDlg("hard")} data-testid="bm-hard-delete-btn"><Trash2 size={14} className="mr-1"/>Delete permanently</Button>}
    </div>
  );
}

const Grid = ({ rows }) => (
  <div className="grid grid-cols-[110px_1fr] gap-y-1.5 gap-x-3">
    {rows.map(([k, v]) => [<span key={k} className="text-slate-500 text-xs">{k}</span>, <span key={k + "v"} className="text-slate-200 break-words">{v}</span>])}
  </div>
);

const Section = ({ title, rows, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">{title}</div>
    {rows.length === 0 ? <div className="text-xs text-slate-600">None</div> : (
      <div className="rounded-lg border border-slate-800 divide-y divide-slate-800">
        {rows.map((r, i) => <div key={i} className="px-3 py-2 flex flex-wrap gap-x-4 text-xs text-slate-300">{r.map((c, j) => <span key={j} className={j === 0 ? "font-mono text-slate-400" : ""}>{c}</span>)}</div>)}
      </div>
    )}
  </div>
);

function CancelQuote({ id }) {
  const [q, setQ] = useState(null);
  useEffect(() => { api.get(`/admin/manage/bookings/${id}/cancellation-quote`).then((r) => setQ(r.data)).catch(() => {}); }, [id]);
  if (!q) return null;
  return (
    <div className="rounded-lg border border-slate-800 p-3 text-xs" data-testid="bm-cancel-quote">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">If cancelled now</div>
      <div className="text-slate-300">{q.hours_before_start > 0 ? `${q.hours_before_start}h before start` : "After scheduled start"} · fee <b className="text-amber-300" data-testid="bm-cancel-fee">{q.fee_pct}% = {inr(q.cancellation_fee)}</b>{q.amount_paid > 0 && <> · refundable {inr(q.refundable_from_paid)}</>}</div>
      <div className="text-slate-500 mt-1">Policy: {q.policy.map((t) => `≥${t.min_hours_before}h → ${t.fee_pct}%`).join(" · ")} ({q.policy_source === "booking_snapshot" ? "locked at booking time" : "current settings"})</div>
    </div>
  );
}
